/**
 * RevealRemotePage.js
 *
 * /fly/custom-declaration-result-remote — the host's phone remote for the Unclaimed Declarations reveal on the TV
 * (/fly/custom-declaration-result). Before the reveal it runs the timer (extend it, end it), reveals who declared
 * every card on guests' phones only (they can still pick the lies until guessing closes), and starts the reveal
 * ("Show the result"). Then each card takes three taps of Next: its statements, then the lie, then who
 * declared it. Then Smuggler of the Year and Immigration Officer of the Year, and finally the host lets guests see
 * their results on their phones.
 * Only the host can use it: signed in with Google, and the backend checks it is an admin (ADMIN_EMAILS).
 * The remote shows exactly what the TV shows, never what's coming next.
 */

import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useClientAuth } from "../../hooks/clientAuth";
import { CARD_INK, CARD_PAPER, CARD_RED, cardLabel } from "./components/DeclarationCard";
import { useWakeLock } from "./ArrivalsPage";
import { getRemote, hostAction, setRemoteStep } from "./helpers/flyApi";
import { formatHms, formatTime, useNoIndex, useTimeLeft } from "./helpers/flyPage";

const MUTED = "#5B6577";
const LINE = "#D9D2C3";
const BACKDROP = "#E9E4DA";
const OK = "#0D6B4F";
const SANS = "'Space Grotesk', system-ui, sans-serif";
const MONO = "'JetBrains Mono', monospace";

// Keeps the remote in step if the TV or /fly/admin moves the reveal too
const POLL_MS = 3000;

const STAGES = ["The three statements", "The lie", "Who declared it"];

/**
 * What the next tap of Next will show
 * @param {Object} reveal   -   { step, steps, card, cardIndex, cardCount, smuggler, officers }
 */
const nextLabel = (reveal) => {
    if (reveal.card) {
        if (reveal.card.stage === 0) return "Show the lie";
        if (reveal.card.stage === 1) return "Show who declared it";
        return reveal.cardIndex + 1 < reveal.cardCount ? `Next card (${reveal.cardIndex + 2} of ${reveal.cardCount})` : "Smuggler of the Year";
    }
    if (reveal.smuggler) return "Officer of the Year";
    return null;
};

/**
 * A full-screen message, with an optional action
 */
const Message = ({ title, body, action }) => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-xl font-bold">{title}</p>
        {body && <p style={{ color: MUTED }}>{body}</p>}
        {action}
    </div>
);

const PrimaryButton = ({ onClick, disabled, children, colour = CARD_INK }) => (
    <button type="button" onClick={onClick} disabled={disabled}
        className="min-h-[56px] w-full rounded-2xl px-5 text-base font-bold tracking-[0.06em] inline-flex items-center justify-center gap-2 disabled:opacity-40"
        style={{ background: colour, color: CARD_PAPER }}>
        {children}
    </button>
);

/**
 * What the TV is showing now: the card's statements (the lie struck once shown) and who declared it, or an award
 */
const NowShowing = ({ reveal }) => {
    const card = reveal.card || reveal.smuggler;
    if (reveal.officers) {
        return (
            <div className="flex flex-col gap-2">
                <p className="text-xs font-bold tracking-[0.2em]" style={{ color: MUTED }}>IMMIGRATION OFFICER OF THE YEAR</p>
                {reveal.officers.length === 0 && <p>No reports were filed.</p>}
                {reveal.officers.map((officer, index) => (
                    <p key={officer.name} className="flex items-center gap-3 text-base">
                        <span className="w-6 font-bold" style={{ fontFamily: MONO }}>{index + 1}.</span>
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: officer.colour }} aria-hidden="true" />
                        <span className="flex-1 font-semibold">{officer.name}</span>
                        <span style={{ fontFamily: MONO }}>{officer.points} pts</span>
                    </p>
                ))}
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <p className="text-xs font-bold tracking-[0.2em]" style={{ color: MUTED }}>
                {reveal.smuggler ? `SMUGGLER OF THE YEAR · No. ${cardLabel(card.number)}` : `No. ${cardLabel(card.number)} · ${STAGES[card.stage].toUpperCase()}`}
            </p>
            {card.statements.map((statement, index) => {
                const isLie = card.stage >= 1 && index === card.lie;
                return (
                    <div key={index} className="rounded-xl border px-3 py-2 text-base leading-snug"
                        style={{ borderColor: isLie ? CARD_RED : LINE, background: isLie ? "#F8E7E2" : "#fff", opacity: card.stage >= 1 && !isLie ? 0.55 : 1 }}>
                        <span className="font-bold mr-2" style={{ fontFamily: MONO }}>{index + 1}</span>
                        <span style={isLie ? { textDecoration: "line-through", color: CARD_RED } : undefined}>{statement}</span>
                        {isLie && <span className="ml-2 text-[10px] font-bold tracking-[0.16em]" style={{ color: CARD_RED }}>THE LIE</span>}
                    </div>
                );
            })}
            {card.stage >= 1 && (
                <p className="text-sm" style={{ color: MUTED }}>{card.fellFor} fell for it · {card.reports} {card.reports === 1 ? "report" : "reports"}</p>
            )}
            {card.passenger && (
                <p className="flex items-center gap-2 text-lg font-bold pt-1">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: card.passenger.colour }} aria-hidden="true" />
                    {card.passenger.name} <span className="text-sm font-medium" style={{ color: MUTED }}>· {card.passenger.city}</span>
                </p>
            )}
        </div>
    );
};

/**
 * Before the reveal: the time left (extend it or end it), then "Show the result" once guessing has closed
 */
const BeforeReveal = ({ board, busy, error, onAct }) => {
    const open = board.phase === "open";
    const left = useTimeLeft(board.closesAt, open);
    if (board.phase === "unlocked" || board.phase === "locked") {
        return <Message title="Guessing hasn't opened yet" body={board.opensAt ? `It opens by itself at ${formatTime(board.opensAt)}. This page will be ready then.` : "This page will be ready once it opens."} />;
    }
    return (
        <>
            <div className="flex-1 flex flex-col items-center justify-center gap-2 px-8 text-center">
                <p className="text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>{open ? "GUESSING CLOSES IN" : "GUESSING IS CLOSED"}</p>
                {open
                    ? <p className="text-5xl font-bold" style={{ fontFamily: MONO }} role="timer">{formatHms(left ?? 0)}</p>
                    : <p className="text-xl font-bold">The reports are in.</p>}
                <p className="text-sm" style={{ color: MUTED }}>{open ? `At ${formatTime(board.closesAt)}` : "Show the result to start the reveal on the TV."}</p>
            </div>
            <div className="flex flex-col gap-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)]" style={{ background: CARD_PAPER, borderTop: `1px solid ${LINE}` }}>
                {error && <p className="text-sm text-center" style={{ color: CARD_RED }} role="alert">{error}</p>}
                {!open && <PrimaryButton onClick={() => onAct("start")} disabled={busy} colour={OK}>Show the result <ChevronRight size={20} aria-hidden="true" /></PrimaryButton>}
                {open && (board.peopleRevealedAt
                    ? <p className="text-sm text-center font-semibold" style={{ color: OK }}>Passengers revealed on phones at {formatTime(board.peopleRevealedAt)}. Guests can still pick the lies.</p>
                    : <PrimaryButton onClick={() => onAct("reveal-people", "Reveal who declared every card, on guests' phones only? Nobody can change their passenger picks after this. They can still pick the lies until the timer ends.")} disabled={busy} colour={OK}>Reveal passengers on phones</PrimaryButton>)}
                {open && <PrimaryButton onClick={() => onAct("close", "End the timer now? Nobody can file or change a report after this.")} disabled={busy} colour={CARD_RED}>End timer</PrimaryButton>}
                <button type="button" onClick={() => onAct("extend")} disabled={busy}
                    className="min-h-[48px] rounded-2xl text-base font-bold disabled:opacity-40" style={{ border: `2px solid ${CARD_INK}` }}>
                    Extend timer +5 min
                </button>
            </div>
        </>
    );
};

/**
 * The remote itself, once the host is signed in
 */
const Remote = ({ initial }) => {
    const [board, setBoard] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [confirmFinish, setConfirmFinish] = useState(false);

    const refresh = useCallback(async () => {
        try {
            setBoard(await getRemote());
            setError("");
        } catch {
            setError("Can't reach the backend. Retrying…");
        }
    }, []);

    useEffect(() => {
        if (busy) return undefined;
        const timer = setInterval(refresh, POLL_MS);
        return () => clearInterval(timer);
    }, [busy, refresh]);

    const reveal = board.reveal;

    const go = async (by) => {
        if (busy || !reveal) return;
        setBusy(true);
        setConfirmFinish(false);
        try {
            await setRemoteStep(reveal.step + by);
            await refresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const finish = async () => {
        if (!confirmFinish) {
            setConfirmFinish(true);
            return;
        }
        setBusy(true);
        try {
            setBoard(await hostAction("finish"));
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
            setConfirmFinish(false);
        }
    };

    // Before the reveal: the timer, and the button that starts the reveal on the TV
    const act = async (action, question) => {
        if (question && !window.confirm(question)) return;
        setBusy(true);
        try {
            setBoard(await hostAction(action));
            setError("");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (!reveal) {
        return <BeforeReveal board={board} busy={busy} error={error} onAct={act} />;
    }
    if (!reveal.card && !reveal.smuggler && !reveal.officers) return <Message title="No cards were locked" body="There's nothing to reveal." />;

    const next = nextLabel(reveal);
    const lastStep = reveal.step >= reveal.steps - 1;

    return (
        <>
            <div className="px-4 pt-3 pb-2" style={{ background: CARD_PAPER, borderBottom: `1px solid ${LINE}` }}>
                <div className="flex items-baseline justify-between gap-3">
                    <p className="text-base font-bold">{reveal.card ? `Card ${reveal.cardIndex + 1} of ${reveal.cardCount}` : "The awards"}</p>
                    <p className="text-xs" style={{ color: MUTED, fontFamily: MONO }}>Step {reveal.step + 1} / {reveal.steps}</p>
                </div>
                <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: BACKDROP }}>
                    <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${((reveal.step + 1) / reveal.steps) * 100}%`, background: CARD_INK }} />
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
                <p className="text-[10px] font-bold tracking-[0.22em] mb-2" style={{ color: MUTED }}>ON THE TV NOW</p>
                <NowShowing reveal={reveal} />
            </div>

            <div className="flex flex-col gap-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)]" style={{ background: CARD_PAPER, borderTop: `1px solid ${LINE}` }}>
                {error && <p className="text-sm text-center" style={{ color: CARD_RED }} role="alert">{error}</p>}
                {!lastStep && (
                    <PrimaryButton onClick={() => go(1)} disabled={busy}>
                        {next} <ChevronRight size={20} aria-hidden="true" />
                    </PrimaryButton>
                )}
                {lastStep && board.phase === "closed" && (
                    <PrimaryButton onClick={finish} disabled={busy} colour={confirmFinish ? CARD_RED : OK}>
                        {confirmFinish ? "Tap again: show everyone their results" : "Show guests their results"}
                    </PrimaryButton>
                )}
                {lastStep && board.phase === "finished" && (
                    <p className="min-h-[56px] flex items-center justify-center text-base font-bold" style={{ color: OK }}>Reveal finished. Guests can see their results.</p>
                )}
                <button type="button" onClick={() => go(-1)} disabled={busy || reveal.step === 0}
                    className="min-h-[44px] inline-flex items-center justify-center gap-1 text-sm font-semibold disabled:opacity-30" style={{ color: MUTED }}>
                    <ChevronLeft size={16} aria-hidden="true" /> Back
                </button>
            </div>
        </>
    );
};

const RevealRemotePage = () => {
    const { user, loading: authLoading, signInWithGoogle, signOutUser } = useClientAuth();
    // 'loading' | 'signin' | 'not_host' | 'error' | 'ready'
    const [state, setState] = useState("loading");
    const [initial, setInitial] = useState(null);

    useNoIndex("Reveal remote · Flight YJ030");
    useWakeLock();

    const load = useCallback(async () => {
        setState("loading");
        try {
            setInitial(await getRemote());
            setState("ready");
        } catch (err) {
            if (err.status === 401) setState("signin");
            else if (err.status === 403) setState("not_host");
            else setState("error");
        }
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user) setState("signin");
        else load();
    }, [authLoading, user, load]);

    const signIn = async () => {
        try {
            if (user) await signOutUser();
            await signInWithGoogle();
        } catch {
            // Closed the popup: stay on this screen
        }
    };

    return (
        <div className="flex flex-col overflow-hidden text-left" style={{ height: "100dvh", background: BACKDROP, color: CARD_INK, fontFamily: SANS }}>
            <header className="px-4 pt-[calc(env(safe-area-inset-top,0px)+10px)] pb-2" style={{ background: CARD_INK, color: CARD_PAPER }}>
                <p className="text-[10px] font-bold tracking-[0.22em] opacity-80">RIGHT BROTHER AIRWAYS · FINAL DESCENT</p>
                <p className="text-base font-bold">Unclaimed Declarations · Reveal remote</p>
            </header>
            {state === "loading" && <Message title="Checking your clearance…" />}
            {state === "signin" && (
                <Message title="Host only" body="Sign in with the host's Google account to control the reveal."
                    action={<div className="w-full max-w-xs"><PrimaryButton onClick={signIn}>Sign in with Google</PrimaryButton></div>} />
            )}
            {state === "not_host" && (
                <Message title="This remote is only for the host" body={user?.email ? `You're signed in as ${user.email}.` : null}
                    action={<button type="button" onClick={signIn} className="min-h-[44px] text-sm underline underline-offset-2">Sign in with a different account</button>} />
            )}
            {state === "error" && (
                <Message title="We couldn't reach the backend."
                    action={<div className="w-full max-w-xs"><PrimaryButton onClick={load}>Try again</PrimaryButton></div>} />
            )}
            {state === "ready" && <Remote initial={initial} />}
        </div>
    );
};

export default RevealRemotePage;
